import React, { useEffect } from 'react'
import { generatePath } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormField,
  FormReadonlyField,
  FormSelectField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ClientDto } from 'api/Clients/Types/Client'
import { PaymentAgentWithdrawalTransaction } from 'api/Transaction/Types/PaymentAgentWithdrawalTransaction'
import { WalletDirectionTypeId } from 'api/Transaction/Types/Transaction'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
} from 'api/Wallet/Types/Wallet'
import { WalletPaymentAgentMethodDto } from 'api/Wallet/Types/WalletPaymentAgentMethodDto'
import { Link } from 'components/Link'
import { Loading } from 'components/Loading'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import toNumber from 'lodash/toNumber'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'

export interface PaymentWithdrawalFormValues {
  client: string | null
  paymentAgentWalletId: string
  clientWalletId: string
  paymentAgentClientId: string
  paymentAgentMethodId: string
  system: string
  account: string
  amount: number
  comment: string | null
}

const PaymentAgentWithdrawalFormUI: React.FC<
  FormikProps<PaymentWithdrawalFormValues> & OuterProps
> = (props) => {
  const { client, values, errors, setFieldValue, isSubmitting } = props

  useEffect(() => {
    props.resetForm()
  }, [client?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Loading isLoading={props.isLoading}>
      <Form>
        <DataGrid columns={1}>
          <DataColumn>
            <DataCard title='Payment Agent Withdrawal' columns={2}>
              <FormReadonlyField
                name='client'
                label='Client'
                value={values.client ?? ''}
                rightAddon={
                  <>
                    <Button type='button' size='sm' variant='outline' onClick={props.setClient}>
                      Select
                    </Button>
                    {client?.id && (
                      <Link
                        to={generatePath(links.recordsClients.detailsUrl as string, {
                          id: client.id,
                          slug: 'details',
                        })}
                        showIcon
                      />
                    )}
                  </>
                }
              />
              <FormSelectField
                name='clientWalletId'
                label='Client Wallet'
                value={values.clientWalletId || undefined}
                options={props.clientWallets
                  .filter((wallet) => wallet.currency.id === 'USD')
                  .map((wallet) => ({
                    id: wallet.id,
                    name: `${wallet.name}-${wallet.walletType.name}-${wallet.balance}`,
                  }))}
                error={errors.clientWalletId}
                onChange={(value) => {
                  const nextWalletId = value ? String(value) : ''
                  setFieldValue('clientWalletId', nextWalletId)
                  props.setClientWalletId(nextWalletId)
                }}
                rightAddon={
                  props.clientWallet?.id && (
                    <Link
                      to={generatePath(links.recordsWallets.detailsUrl as string, {
                        id: props.clientWallet.id,
                        slug: 'details',
                      })}
                      showIcon
                    />
                  )
                }
              />
              <FormSelectField
                name='paymentAgentMethodId'
                label='Payment Agent Method'
                value={values.paymentAgentMethodId || undefined}
                options={props.paymentAgents.map((agent) => ({
                  id: agent.methodId,
                  name: agent.name ?? '',
                }))}
                error={errors.paymentAgentClientId}
                onChange={(value) => {
                  const nextMethodId = value ? String(value) : ''
                  const paymentAgentMethod = props.paymentAgents.find(
                    (agent) => agent.methodId === nextMethodId
                  )
                  const nextPaymentAgentClientId =
                    paymentAgentMethod?.paymentAgent?.client?.id || ''

                  setFieldValue('paymentAgentMethodId', nextMethodId)
                  setFieldValue('paymentAgentClientId', nextPaymentAgentClientId)
                  setFieldValue('paymentAgentWalletId', '')
                  props.setPaymentAgentClientId(nextPaymentAgentClientId)

                  if (nextPaymentAgentClientId) {
                    props.getPaymentAgentWallets(nextPaymentAgentClientId)
                  }
                }}
              />
              <FormSelectField
                name='paymentAgentWalletId'
                label='Payment Agent Wallet'
                value={values.paymentAgentWalletId || undefined}
                options={props.paymentAgentWallets
                  .filter((wallet) => wallet.walletType.id === WalletDirectionTypeId.DEBIT)
                  .map((wallet) => ({
                    id: wallet.id,
                    name: `${wallet.name}-${wallet.walletType.name}-${wallet.balance}`,
                  }))}
                error={errors.paymentAgentWalletId}
                onChange={(value) => {
                  const nextWalletId = value ? String(value) : ''
                  setFieldValue('paymentAgentWalletId', nextWalletId)
                  props.setPaymentAgentWalletId(nextWalletId)
                }}
                rightAddon={
                  values.paymentAgentWalletId && (
                    <Link
                      to={generatePath(links.recordsWallets.detailsUrl as string, {
                        id: values.paymentAgentWalletId,
                        slug: 'details',
                      })}
                      showIcon
                    />
                  )
                }
              />
              <FormField
                name='amount'
                label='Amount'
                type='number'
                error={errors.amount}
                rightAddon={
                  props.currency ? (
                    <span className='inline-flex items-center px-3 text-sm text-muted-foreground'>
                      {props.currency}
                    </span>
                  ) : undefined
                }
              />
              <FormField name='system' label='Bank Name' error={errors.system} />
              <FormField
                name='account'
                label='IBAN or Bank Account Number'
                error={errors.account}
              />
              <FormTextareaField name='comment' label='Comment' rows={3} maxLength={130} />
            </DataCard>
          </DataColumn>
        </DataGrid>
        <div className='mt-4 flex justify-center'>
          <Button disabled={isSubmitting} type='submit'>
            Submit
          </Button>
        </div>
      </Form>
    </Loading>
  )
}

function dataToSubmit(
  values: PaymentWithdrawalFormValues
): FormSubmitValues<PaymentAgentWithdrawalTransaction> {
  return {
    amount: toNumber(values.amount),
    comment: values.comment,
    account: values.account,
    clientWalletId: values.clientWalletId,
    paymentAgentWalletId: values.paymentAgentWalletId,
    paymentAgentMethodId: values.paymentAgentMethodId,
    system: values.system,
  }
}

interface OuterProps {
  isLoading: boolean
  paymentAgents: WalletPaymentAgentMethodDto[]
  client: ClientDto | null
  clientWallet: WalletDto | undefined
  paymentAgentWalletId: string
  paymentAgentClientId: string
  clientWallets: WalletDto[]
  paymentAgentWallets: WalletDto[]
  currency: null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string

  setForm(values: PaymentWithdrawalFormValues): void
  onSubmit(values: FormSubmitValues<PaymentAgentWithdrawalTransaction>): Promise<void>
  setClient(): void
  getClientWallets(clientId: string): void
  setClientWalletId(walletId: string): void
  setPaymentAgentWalletId(walletId: string): void
  setPaymentAgentClientId(clientId: string): void
  getPaymentAgentWallets(paymentAgentClientId: string): void
}

export const PaymentAgentWithdrawalForm = withFormik<OuterProps, PaymentWithdrawalFormValues>({
  mapPropsToValues: ({ client, clientWallet, paymentAgentClientId, paymentAgentWalletId }) => ({
    client: client?.name || null,
    paymentAgentWalletId,
    clientWalletId: clientWallet?.id || '',
    paymentAgentClientId,
    paymentAgentMethodId: '',
    account: '',
    amount: 0,
    comment: null,
    system: '',
  }),
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      props.setForm(values)
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<PaymentWithdrawalFormValues> = {}

    if (!values.paymentAgentWalletId) {
      errors.paymentAgentWalletId = 'Required'
    }

    if (!values.clientWalletId) {
      errors.clientWalletId = 'Required'
    }

    if (!values.paymentAgentClientId) {
      errors.paymentAgentClientId = 'Required'
    }

    if (!values.amount) {
      errors.amount = 'Required'
    } else if (toNumber(values.amount) <= 0) {
      errors.amount = 'Must be greater than 0'
    }

    if (!values.client) {
      errors.client = 'Required'
    }

    if (!values.system) {
      errors.system = 'Required'
    }

    if (!values.account) {
      errors.account = 'Required'
    }

    return errors
  },
})(PaymentAgentWithdrawalFormUI)
